import { useState, useEffect } from "react";
import { Heart, Users, Calendar, Target, X, ChevronLeft, ChevronRight, Eye, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";

import { API } from "../config/api";

// Helper to normalize image URLs
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    if (url.includes(".preview.emergentagent.com/api/uploads/")) {
      const filename = url.split("/api/uploads/").pop();
      return `/api/uploads/${filename}`;
    }
    return url;
  }
  if (url.startsWith("/")) return url;
  return "/" + url;
};

const ICON_MAP = { Heart, Users, Calendar, Target, Eye, BookOpen };

export default function AboutPage() {
  const [pageData, setPageData] = useState(null);
  const [aboutImages, setAboutImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/site-pages/about`).then(res => res.json()),
      fetch(`${API}/about/images`).then(res => res.json())
    ])
      .then(([pageRes, imagesRes]) => {
        if (!pageRes.not_found) setPageData(pageRes);
        setAboutImages(imagesRes || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openLightbox = (idx) => {
    setCurrentImageIdx(idx);
    setLightboxOpen(true);
  };

  const navigateLightbox = (delta) => {
    setCurrentImageIdx(prev => {
      const newIdx = prev + delta;
      if (newIdx < 0) return aboutImages.length - 1;
      if (newIdx >= aboutImages.length) return 0;
      return newIdx;
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") navigateLightbox(-1);
      if (e.key === "ArrowRight") navigateLightbox(1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, aboutImages.length]);

  // Helper to get section by type
  const getSection = (type) => pageData?.sections?.find(s => s.type === type);

  const heroSection = getSection("hero") || { heading: "About Tatvamasi", subheading: "Our Story", body: "Kerala Hindu's of London Ontario" };
  const mainSection = getSection("main") || { heading: "Preserving Heritage, Building Community", body: "Content coming soon...", image_url: "https://images.unsplash.com/photo-1608949791834-ed30e0687dd2?w=800" };
  const valuesSection = getSection("values") || { items: [] };
  const missionSection = getSection("mission") || {};
  const milestonesSection = getSection("milestones") || { items: [] };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-kerala-cream" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-kerala-green">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">{heroSection.subheading}</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white mb-6">
            {heroSection.heading}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {heroSection.body}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl text-kerala-green mb-6">
                {mainSection.heading}
              </h2>
              <div className="prose text-gray-600 space-y-4">
                {mainSection.body?.split('\n\n').map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={getImageUrl(mainSection.image_url) || "https://images.unsplash.com/photo-1608949791834-ed30e0687dd2?w=800"}
                alt="Traditional Kerala Lamp"
                className="rounded-sm shadow-xl"
              />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-kerala-gold/20 rounded-sm -z-10" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-kerala-green/10 rounded-sm -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      {valuesSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt">
          <div className="section-container">
            <div className="text-center mb-16">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">{valuesSection.subheading || "What We Stand For"}</p>
              <h2 className="font-heading text-4xl text-kerala-green">
                {valuesSection.heading || "Our Values"}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {valuesSection.items.map((value, idx) => {
                const IconComponent = ICON_MAP[value.icon] || Heart;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 bg-kerala-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-kerala-green" />
                    </div>
                    <h3 className="font-heading text-xl text-kerala-green mb-2">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Mission */}
      {missionSection.heading && (
        <section className="section-padding bg-kerala-green">
          <div className="section-container text-center max-w-4xl">
            <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">Our Mission</p>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              {missionSection.body}
            </h2>
            <p className="text-white/70">
              {missionSection.subheading}
            </p>
          </div>
        </section>
      )}

      {/* Community Photos Gallery */}
      {aboutImages.length > 0 && (
        <section className="section-padding bg-kerala-cream" data-testid="about-gallery">
          <div className="section-container">
            <div className="text-center mb-12">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">Our Community</p>
              <h2 className="font-heading text-4xl text-kerala-green">
                Community Photos
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {aboutImages.map((image, idx) => (
                <div
                  key={image.id}
                  className="relative overflow-hidden rounded-sm cursor-pointer group aspect-square"
                  onClick={() => openLightbox(idx)}
                  data-testid={`about-photo-${idx}`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-kerala-green/0 group-hover:bg-kerala-green/40 transition-colors duration-300 flex items-end">
                    <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white font-medium">{image.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxOpen && aboutImages[currentImageIdx] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="about-lightbox">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/10"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-6 h-6" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
            onClick={() => navigateLightbox(-1)}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
            onClick={() => navigateLightbox(1)}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>

          <div className="max-w-5xl max-h-[80vh] px-4">
            <img
              src={aboutImages[currentImageIdx].url}
              alt={aboutImages[currentImageIdx].title}
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
            <div className="text-center mt-4">
              <p className="text-white font-medium">{aboutImages[currentImageIdx].title}</p>
              <p className="text-white/40 text-sm mt-2">
                {currentImageIdx + 1} / {aboutImages.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {milestonesSection.items?.length > 0 && (
        <section className="section-padding">
          <div className="section-container max-w-3xl">
            <div className="text-center mb-16">
              <p className="text-kerala-terracotta text-sm tracking-widest uppercase mb-2">{milestonesSection.subheading || "Our Journey"}</p>
              <h2 className="font-heading text-4xl text-kerala-green">
                {milestonesSection.heading || "Milestones"}
              </h2>
            </div>

            <div className="space-y-8">
              {milestonesSection.items.map((milestone, idx) => (
                <div key={idx} className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-kerala-gold rounded-full flex items-center justify-center shrink-0">
                      <span className="font-heading text-kerala-green font-bold">{milestone.year}</span>
                    </div>
                    {idx < milestonesSection.items.length - 1 && <div className="w-0.5 h-full bg-kerala-gold/30 mt-2" />}
                  </div>
                  <div className="pb-8">
                    <h3 className="font-heading text-xl text-kerala-green mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
